import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Person as PersonIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  School as SchoolIcon,
  People as PeopleIcon,
  MenuBook as TeacherIcon,
} from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { login } from '../../store/slices/authSlice';
import { useAppDispatch, useAppSelector } from '../../store/hooks';

const schema = yup.object({
  username: yup.string().required('Employee Number or Username is required'),
  password: yup.string().required('Password is required'),
});

interface LoginForm {
  username: string;
  password: string;
}

const floatingKeyframes = `
@keyframes float1 {
  0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.08; }
  50% { transform: translate(30px, -40px) scale(1.1); opacity: 0.15; }
}
@keyframes float2 {
  0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.06; }
  50% { transform: translate(-20px, 30px) scale(1.2); opacity: 0.12; }
}
@keyframes float3 {
  0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.05; }
  50% { transform: translate(15px, 20px) scale(0.9); opacity: 0.1; }
}
@keyframes fadeInUp {
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes shimmer {
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
}
`;

const Login: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const sessionExpired = searchParams.get('expired') === '1';
  const { loading, error, isAuthenticated } = useAppSelector((state) => state.auth);
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>({
    resolver: yupResolver(schema),
  });

  React.useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  const onSubmit = async (data: LoginForm) => {
    try {
      const isEmployeeNumber = /^\d+$/.test(data.username);
      if (isEmployeeNumber) {
        await dispatch(login({
          empNo: data.username,
          userPassword: data.password
        } as any)).unwrap();
      } else {
        await dispatch(login(data)).unwrap();
      }
      navigate('/');
    } catch (error) {
      // Error is handled by the slice
    }
  };

  const stats = [
    { icon: <SchoolIcon sx={{ fontSize: 28 }} />, value: '1,510+', label: 'Schools' },
    { icon: <PeopleIcon sx={{ fontSize: 28 }} />, value: '510,000+', label: 'Students' },
    { icon: <TeacherIcon sx={{ fontSize: 28 }} />, value: '17,000+', label: 'Teachers' },
  ];

  return (
    <>
      <style>{floatingKeyframes}</style>
      <Box sx={{ display: 'flex', minHeight: '100vh', flexDirection: { xs: 'column', md: 'row' } }}>

        {/* Left Panel - Government Branding */}
        <Box
          sx={{
            flex: { xs: 'none', md: '0 0 58%' },
            background: 'linear-gradient(135deg, #0d47a1 0%, #1565c0 30%, #00796b 70%, #004d40 100%)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'relative',
            overflow: 'hidden',
            px: { xs: 3, md: 6 },
            py: { xs: 5, md: 0 },
            minHeight: { xs: 320, md: 'auto' },
          }}
        >
          {/* Decorative floating elements */}
          {[
            { size: 300, top: '-5%', left: '-8%', anim: 'float1 12s ease-in-out infinite' },
            { size: 200, bottom: '10%', right: '-5%', anim: 'float2 15s ease-in-out infinite' },
            { size: 150, top: '60%', left: '10%', anim: 'float3 10s ease-in-out infinite' },
            { size: 100, top: '15%', right: '15%', anim: 'float2 18s ease-in-out infinite' },
            { size: 80, bottom: '30%', left: '50%', anim: 'float1 14s ease-in-out infinite' },
          ].map((circle, i) => (
            <Box
              key={i}
              sx={{
                position: 'absolute',
                width: circle.size,
                height: circle.size,
                borderRadius: '50%',
                border: '2px solid rgba(255,255,255,0.08)',
                background: 'radial-gradient(circle, rgba(255,255,255,0.05) 0%, transparent 70%)',
                top: circle.top,
                bottom: circle.bottom,
                left: circle.left,
                right: circle.right,
                animation: circle.anim,
                pointerEvents: 'none',
              }}
            />
          ))}

          {/* Content */}
          <Box
            sx={{
              position: 'relative',
              zIndex: 1,
              textAlign: 'center',
              animation: 'fadeInUp 0.8s ease-out',
              maxWidth: 500,
            }}
          >
            <Box
              component="img"
              src="/smz-logo.png"
              alt="SMZ Coat of Arms"
              sx={{
                width: { xs: 90, md: 130 },
                height: 'auto',
                mb: 3,
                filter: 'drop-shadow(0 4px 20px rgba(0,0,0,0.3))',
              }}
            />

            <Typography
              variant="h5"
              sx={{
                color: 'white',
                fontWeight: 700,
                lineHeight: 1.3,
                mb: 1,
                fontSize: { xs: '1.2rem', md: '1.5rem' },
                textShadow: '0 2px 10px rgba(0,0,0,0.2)',
              }}
            >
              Ministry of Education and Vocational Training
            </Typography>

            <Typography
              variant="subtitle1"
              sx={{
                color: 'rgba(255,255,255,0.85)',
                fontWeight: 400,
                mb: 3,
                fontSize: { xs: '0.85rem', md: '1rem' },
              }}
            >
              The Revolutionary Government of Zanzibar
            </Typography>

            <Box
              sx={{
                width: 60,
                height: 3,
                bgcolor: 'rgba(255,255,255,0.4)',
                borderRadius: 2,
                mx: 'auto',
                mb: 3,
              }}
            />

            <Typography
              variant="body2"
              sx={{
                color: 'rgba(255,255,255,0.7)',
                maxWidth: 380,
                mx: 'auto',
                lineHeight: 1.6,
                display: { xs: 'none', md: 'block' },
              }}
            >
              Empowering Zanzibar's education system with real-time data analytics,
              enrollment tracking, and evidence-based decision making.
            </Typography>
          </Box>

          {/* Stats Bar */}
          <Box
            sx={{
              position: { xs: 'relative', md: 'absolute' },
              bottom: { md: 0 },
              left: { md: 0 },
              right: { md: 0 },
              mt: { xs: 4, md: 0 },
              display: 'flex',
              justifyContent: 'center',
              gap: { xs: 2, md: 0 },
              bgcolor: 'rgba(0,0,0,0.15)',
              backdropFilter: 'blur(10px)',
              borderTop: '1px solid rgba(255,255,255,0.1)',
              py: 2.5,
              px: 2,
              animation: 'fadeInUp 1s ease-out 0.3s both',
            }}
          >
            {stats.map((stat, i) => (
              <Box
                key={i}
                sx={{
                  textAlign: 'center',
                  flex: { md: 1 },
                  px: { xs: 1.5, md: 3 },
                  borderRight: i < stats.length - 1 ? { md: '1px solid rgba(255,255,255,0.15)' } : 'none',
                }}
              >
                <Box sx={{ color: 'rgba(255,255,255,0.7)', mb: 0.5 }}>{stat.icon}</Box>
                <Typography
                  variant="h6"
                  sx={{
                    color: 'white',
                    fontWeight: 700,
                    fontSize: { xs: '1rem', md: '1.25rem' },
                    lineHeight: 1.2,
                  }}
                >
                  {stat.value}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.7rem' }}
                >
                  {stat.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Right Panel - Login Form */}
        <Box
          sx={{
            flex: { xs: 1, md: '0 0 42%' },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            bgcolor: '#fafbfc',
            px: { xs: 3, sm: 5, md: 6 },
            py: { xs: 4, md: 0 },
            position: 'relative',
          }}
        >
          {/* Subtle top accent line */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: 'linear-gradient(90deg, #0d47a1, #00796b)',
              display: { xs: 'none', md: 'block' },
            }}
          />

          <Box
            sx={{
              width: '100%',
              maxWidth: 400,
              animation: 'fadeInUp 0.6s ease-out 0.2s both',
            }}
          >
            {/* Form Header */}
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: '#1a237e',
                  mb: 0.5,
                  fontSize: { xs: '1.6rem', md: '2rem' },
                }}
              >
                EMIS Toolbox
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Sign in to your account
              </Typography>
            </Box>

            {sessionExpired && !error && (
              <Alert
                severity="warning"
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  '& .MuiAlert-message': { width: '100%' },
                }}
              >
                Your session expired due to inactivity. Please sign in again.
              </Alert>
            )}

            {error && (
              <Alert
                severity="error"
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  '& .MuiAlert-message': { width: '100%' },
                }}
              >
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit(onSubmit)}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="username"
                label="Employee Number"
                placeholder="e.g., 516905"
                autoComplete="username"
                autoFocus
                {...register('username')}
                error={!!errors.username}
                helperText={errors.username?.message || 'Enter your MOE workforce employee number'}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon sx={{ color: 'action.active' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 1,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: 'white',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#1565c0',
                    },
                  },
                }}
              />

              <TextField
                margin="normal"
                required
                fullWidth
                label="Password"
                type={showPassword ? 'text' : 'password'}
                id="password"
                autoComplete="current-password"
                {...register('password')}
                error={!!errors.password}
                helperText={errors.password?.message}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon sx={{ color: 'action.active' }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        size="small"
                      >
                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 1,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: 'white',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#1565c0',
                    },
                  },
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                sx={{
                  mt: 3,
                  mb: 2,
                  py: 1.5,
                  borderRadius: 2,
                  fontSize: '1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  background: 'linear-gradient(135deg, #0d47a1 0%, #00796b 100%)',
                  boxShadow: '0 4px 15px rgba(13, 71, 161, 0.3)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1565c0 0%, #00897b 100%)',
                    boxShadow: '0 6px 20px rgba(13, 71, 161, 0.4)',
                    transform: 'translateY(-1px)',
                  },
                  '&:active': {
                    transform: 'translateY(0)',
                  },
                  '&.Mui-disabled': {
                    background: 'linear-gradient(135deg, #90caf9 0%, #80cbc4 100%)',
                  },
                }}
              >
                {loading ? (
                  <CircularProgress size={24} sx={{ color: 'white' }} />
                ) : (
                  'Sign In'
                )}
              </Button>
            </Box>

            {/* Footer */}
            <Box sx={{ mt: 5, textAlign: 'center' }}>
              <Box
                sx={{
                  width: 40,
                  height: 2,
                  bgcolor: '#e0e0e0',
                  borderRadius: 1,
                  mx: 'auto',
                  mb: 2,
                }}
              />
              <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', lineHeight: 1.8 }}>
                Wizara ya Elimu na Mafunzo ya Amali
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block' }}>
                Serikali ya Mapinduzi ya Zanzibar
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default Login;
